import { OlympiadsApi } from 'api';
import { Api } from 'api/cabmun-api-public';
import useUserData from 'hooks/useUserData';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { fetchOlympsList } from './helpers';
import { Olympiad, OlympiadStatus, ReducerAction, TotalStatistic } from './model';

export const olympStatusValues = [
  { id: OlympiadStatus.current, label: 'Проходят' },
  { id: OlympiadStatus.future, label: 'Пройдут' },
  { id: OlympiadStatus.passed, label: 'Завершились' },
];

export const educationYears = [
  { id: '2025/2026', label: '2025–2026' },
  { id: '2024/2025', label: '2024–2025' },
  { id: '2023/2024', label: '2023–2024' },
  { id: '2022/2023', label: '2022–2023' },
  { id: '2021/2022', label: '2021–2022' },
];

const currentEducationYear = '2025/2026';

type OlymplistState = Record<
  string,
  {
    pending: boolean;
    loaded: boolean;
    error: boolean;
    olympiads: Olympiad[];
    ratingsByOlympiads: Api.GetMunicipalityRatingsByOlympiads200Response[];
  }
>;
const defaultState: OlymplistState[string] = {
  pending: false,
  loaded: false,
  error: false,
  olympiads: [],
  ratingsByOlympiads: [],
};
function reducer(state: OlymplistState, action: ReducerAction) {
  if (action.type === 'startLoading') {
    return {
      ...state,
      [action.key]: { ...(state[action.key] || defaultState), pending: true, loaded: false, error: false },
    };
  }
  if (action.type === 'errorLoading') {
    return {
      ...state,
      [action.key]: {
        ...(state[action.key] || defaultState),
        pending: false,
        loaded: true,
        error: true,
        olympiads: [],
        ratingsByOlympiads: [],
      },
    };
  }
  if (action.type === 'setOlympiadsByKey') {
    return {
      ...state,
      [action.key]: {
        ...(state[action.key] || defaultState),
        pending: false,
        loaded: true,
        error: false,
        olympiads: action.data.olympiads,
        ratingsByOlympiads: action.data.ratingsByOlympiads,
      },
    };
  }
  return state;
}

export const useOlympiadStore = () => {
  const [pendingTotal, setPendingTotal] = useState(false);
  const [currentYear, setCurrentYear] = useState(educationYears[0]);
  const [olympStatus, setOlympStatus] = useState(olympStatusValues[0]);
  const [state, dispatch] = useReducer(reducer, {});
  const [totalStatistic, setTotalStatistic] = useState<TotalStatistic | null>(null);
  const userData = useUserData();
  const selectedCurrentYear = currentYear.id === currentEducationYear;
  const key: string = useMemo(
    () => `${currentYear.id}#${currentYear.id === currentEducationYear ? olympStatus.id : OlympiadStatus.passed}`,
    [olympStatus, currentYear],
  );
  const [currentOlympList, setCurrentOlympList] = useState(defaultState);
  useEffect(() => {
    if (!state[key]?.loaded && !state[key]?.pending) {
      fetchOlympsList({
        key,
        isRegion: !!(userData?.isLargeCity || userData?.isRegion),
        educationYarn: currentYear.id,
        olympiadStatus: currentYear.id === currentEducationYear ? olympStatus.id : OlympiadStatus.passed,
        dispatch,
        cabmunId: userData?.id,
      });
    }
  }, [key]);

  useEffect(() => {
    const fetchData = async () => {
      setPendingTotal(true);
      try {
        const {
          planned,
          completed,
          remaining,
          current,
          active_students_count: activeStudentsCount,
          median_activity: medianActivity,
          students_per_olympiad: studentsPerOlympiad,
          prizeholders_percent: prizeholdersPercent,
          prizeholders_total: prizeholdersTotal,
        } = await OlympiadsApi.getTotalStatistic(currentYear.id);

        setTotalStatistic({
          planned,
          completed,
          remaining,
          current,
          activeStudentsCount,
          medianActivity: medianActivity ?? 0,
          studentsPerOlympiad: studentsPerOlympiad ?? 0,
          prizeholdersPercent: prizeholdersPercent ?? 0,
          prizeholdersTotal: prizeholdersTotal ?? 0,
        });
      } catch (e) {}
      setPendingTotal(false);
    };
    fetchData();
  }, [currentYear]);

  useEffect(() => {
    return setCurrentOlympList(
      state[key]
        ? {
            ...state[key],
            olympiads: state[key].olympiads.map((el) => ({
              ...el,
              ratings: state[key].ratingsByOlympiads.find((r) => r.olympiad_id === el.id)?.ratings,
            })),
          }
        : defaultState,
    );
  }, [key, state]);

  return {
    currentYear,
    setCurrentYear,
    olympStatus: olympStatusValues.find(
      (el) => el.id === (currentYear.id === currentEducationYear ? olympStatus.id : OlympiadStatus.passed),
    )!,
    setOlympStatus,
    pendingTotal,
    totalStatistic,
    currentOlympList,
    selectedCurrentYear,
  };
};

export type OlympiadStore = ReturnType<typeof useOlympiadStore>;
export { OlympiadStatus };
